// Read hooks for the audit trail (per-transaction timeline + global feed).
//
// Same role-scoped split as useTransactions: an admin reads through the
// admin-gated connector ops directly; everyone else goes through the server-side
// scoped proxy (fetchMy*), keyed off the verified token email so a reviewer cannot
// widen it. The fetch decision uses the REAL role, so an admin previewing as
// another role does not refetch; the `enabled` guard withholds the query until auth
// has resolved (a keyless fetch before then would 401).
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import {
  fetchTransactionHistory,
  fetchMyTransactionHistory,
  fetchEvaluationSnapshotDetail,
  fetchMyEvaluationSnapshotDetail,
  fetchAuditFeed,
  fetchMyAuditFeed,
  type TransactionHistory,
  type SnapshotDetail,
  type AuditFeed,
} from '../lib/db';
import { useAuth } from '../lib/auth';

/** One transaction's timeline (audit entries + CES-snapshot summaries). */
export function useTransactionHistory(referenceNo: string) {
  const { realRole, user } = useAuth();
  const isAdmin = realRole === 'admin';
  const email = user?.email ?? '';
  const ready = !!realRole && email.length > 0;
  return useQuery<TransactionHistory, Error>({
    queryKey: isAdmin
      ? queryKeys.auditTrailByRef(referenceNo)
      : queryKeys.scopedAuditTrailByRef(email, referenceNo),
    queryFn: () =>
      isAdmin ? fetchTransactionHistory(referenceNo) : fetchMyTransactionHistory(referenceNo),
    enabled: ready && referenceNo.length > 0,
  });
}

/**
 * The captured blobs for one or two snapshots — fetched only on flyout OPEN (pass
 * `{ enabled: isOpen }`), never on list render, so the timeline never ships blobs.
 */
export function useEvaluationSnapshotDetail(
  ids: readonly string[],
  opts: { enabled?: boolean } = {},
) {
  const { realRole, user } = useAuth();
  const isAdmin = realRole === 'admin';
  const email = user?.email ?? '';
  const ready = !!realRole && email.length > 0;
  return useQuery<SnapshotDetail[], Error>({
    queryKey: queryKeys.evaluationSnapshot(ids),
    queryFn: () =>
      isAdmin ? fetchEvaluationSnapshotDetail([...ids]) : fetchMyEvaluationSnapshotDetail([...ids]),
    enabled: ready && opts.enabled !== false && ids.length > 0,
  });
}

/** The global audit feed for a date window (the Audit Trail screen). */
export function useAuditFeed(range: { from: string; to: string }) {
  const { realRole, user } = useAuth();
  const isAdmin = realRole === 'admin';
  const email = user?.email ?? '';
  const ready = !!realRole && email.length > 0;
  return useQuery<AuditFeed, Error>({
    queryKey: queryKeys.auditFeed(range.from, range.to),
    queryFn: () =>
      isAdmin ? fetchAuditFeed(range.from, range.to) : fetchMyAuditFeed(range.from, range.to),
    enabled: ready && range.from.length > 0 && range.to.length > 0,
  });
}
